import { useState } from 'react';
import {
  useCatalogosProducto,
  useCrearCategoriaProducto,
  useCrearUnidadMedida,
  useEstadoCategoriaProducto,
  useEstadoUnidadMedida,
} from '../api/queries-finanzas';
import { RulerIcon, TagIcon } from './icons';
import { useToast } from '../ui/ToastProvider';

interface CatalogosPanelProps {
  canManage: boolean;
}

export function CatalogosPanel({ canManage }: CatalogosPanelProps) {
  const { notify } = useToast();
  const catalogos = useCatalogosProducto();
  const crearCategoria = useCrearCategoriaProducto();
  const crearUnidad = useCrearUnidadMedida();
  const estadoCategoria = useEstadoCategoriaProducto();
  const estadoUnidad = useEstadoUnidadMedida();

  const [categoriaDraft, setCategoriaDraft] = useState({ nombre: '', descripcion: '' });
  const [unidadDraft, setUnidadDraft] = useState({
    codigo: '',
    nombre: '',
    permiteDecimales: false,
  });
  const [busyId, setBusyId] = useState<string | null>(null);

  const categorias = catalogos.data?.categorias ?? [];
  const unidades = catalogos.data?.unidades ?? [];

  const submitCategoria = async () => {
    if (categoriaDraft.nombre.trim().length < 2) {
      notify('Escribe un nombre de categoría de al menos 2 caracteres.', 'warning');
      return;
    }
    try {
      const response = await crearCategoria.mutateAsync({
        nombre: categoriaDraft.nombre.trim(),
        ...(categoriaDraft.descripcion.trim()
          ? { descripcion: categoriaDraft.descripcion.trim() }
          : {}),
      });
      setCategoriaDraft({ nombre: '', descripcion: '' });
      notify(`Categoría «${response.data.nombre}» creada.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la categoría.', 'error');
    }
  };

  const submitUnidad = async () => {
    if (!/^[A-Za-z0-9._-]{1,10}$/.test(unidadDraft.codigo.trim())) {
      notify('El código debe tener entre 1 y 10 caracteres válidos.', 'warning');
      return;
    }
    if (unidadDraft.nombre.trim().length < 2) {
      notify('Escribe el nombre de la unidad.', 'warning');
      return;
    }
    try {
      const response = await crearUnidad.mutateAsync({
        codigo: unidadDraft.codigo.trim().toUpperCase(),
        nombre: unidadDraft.nombre.trim(),
        permiteDecimales: unidadDraft.permiteDecimales,
      });
      setUnidadDraft({ codigo: '', nombre: '', permiteDecimales: false });
      notify(`Unidad «${response.data.codigo}» creada.`, 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo crear la unidad.', 'error');
    }
  };

  const alternarCategoria = async (id: string, activo: boolean) => {
    setBusyId(id);
    try {
      await estadoCategoria.mutateAsync({ id, activo });
      notify(activo ? 'Categoría reactivada.' : 'Categoría desactivada.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo actualizar la categoría.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  const alternarUnidad = async (id: string, activo: boolean) => {
    setBusyId(id);
    try {
      await estadoUnidad.mutateAsync({ id, activo });
      notify(activo ? 'Unidad reactivada.' : 'Unidad desactivada.', 'success');
    } catch (error) {
      notify(error instanceof Error ? error.message : 'No se pudo actualizar la unidad.', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="catalogos-grid">
      <section className="card catalogo-card">
        <div className="card-head">
          <div className="catalogo-card__title">
            <span className="catalogo-card__icon">
              <TagIcon size={18} />
            </span>
            <div>
              <h2 className="card-title">Categorías de producto</h2>
              <p className="card-sub">Agrupan artículos y servicios para reportes y filtros.</p>
            </div>
          </div>
          <span className="pill pill--soft">{categorias.length} en total</span>
        </div>

        {canManage ? (
          <form
            className="catalogo-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitCategoria();
            }}
          >
            <div className="catalogo-form__fields">
              <label className="field">
                <span className="field-label">Nueva categoría</span>
                <input
                  className="input"
                  value={categoriaDraft.nombre}
                  onChange={(event) =>
                    setCategoriaDraft({ ...categoriaDraft, nombre: event.target.value })
                  }
                  placeholder="Ej. Nutrición foliar"
                />
              </label>
              <label className="field">
                <span className="field-label">Descripción</span>
                <input
                  className="input"
                  value={categoriaDraft.descripcion}
                  onChange={(event) =>
                    setCategoriaDraft({ ...categoriaDraft, descripcion: event.target.value })
                  }
                  placeholder="Opcional"
                />
              </label>
            </div>
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={crearCategoria.isPending}
            >
              {crearCategoria.isPending ? 'Creando…' : 'Agregar categoría'}
            </button>
          </form>
        ) : null}

        <div className="catalogo-list">
          {catalogos.isLoading ? (
            <div className="table-empty">Cargando categorías…</div>
          ) : null}
          {!catalogos.isLoading && categorias.length === 0 ? (
            <div className="table-empty">Sin categorías registradas.</div>
          ) : null}
          {categorias.map((categoria) => (
            <div
              key={categoria.id}
              className={`catalogo-row${categoria.activo ? '' : ' is-inactive'}`}
            >
              <div className="catalogo-row__main">
                <strong>{categoria.nombre}</strong>
                <span>{categoria.descripcion ?? 'Sin descripción'}</span>
              </div>
              <span className="pill pill--soft">{categoria.productos} prod.</span>
              {canManage ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busyId === categoria.id}
                  onClick={() => void alternarCategoria(categoria.id, !categoria.activo)}
                >
                  {categoria.activo ? 'Desactivar' : 'Reactivar'}
                </button>
              ) : (
                <span className="status-badge status-badge--neutral">
                  {categoria.activo ? 'Activa' : 'Inactiva'}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="card catalogo-card">
        <div className="card-head">
          <div className="catalogo-card__title">
            <span className="catalogo-card__icon">
              <RulerIcon size={18} />
            </span>
            <div>
              <h2 className="card-title">Unidades de medida</h2>
              <p className="card-sub">Definen cómo se vende, compra y controla el stock.</p>
            </div>
          </div>
          <span className="pill pill--soft">{unidades.length} en total</span>
        </div>

        {canManage ? (
          <form
            className="catalogo-form"
            onSubmit={(event) => {
              event.preventDefault();
              void submitUnidad();
            }}
          >
            <div className="catalogo-form__fields catalogo-form__fields--unit">
              <label className="field">
                <span className="field-label">Código</span>
                <input
                  className="input"
                  value={unidadDraft.codigo}
                  onChange={(event) =>
                    setUnidadDraft({ ...unidadDraft, codigo: event.target.value.toUpperCase() })
                  }
                  placeholder="BULTO"
                  maxLength={10}
                />
              </label>
              <label className="field">
                <span className="field-label">Nombre</span>
                <input
                  className="input"
                  value={unidadDraft.nombre}
                  onChange={(event) =>
                    setUnidadDraft({ ...unidadDraft, nombre: event.target.value })
                  }
                  placeholder="Bulto de 25 kg"
                />
              </label>
              <label className="checkbox checkbox--compact">
                <input
                  type="checkbox"
                  checked={unidadDraft.permiteDecimales}
                  onChange={(event) =>
                    setUnidadDraft({ ...unidadDraft, permiteDecimales: event.target.checked })
                  }
                />
                Decimales
              </label>
            </div>
            <button
              type="submit"
              className="btn btn--primary btn--sm"
              disabled={crearUnidad.isPending}
            >
              {crearUnidad.isPending ? 'Creando…' : 'Agregar unidad'}
            </button>
          </form>
        ) : null}

        <div className="catalogo-list">
          {catalogos.isLoading ? <div className="table-empty">Cargando unidades…</div> : null}
          {!catalogos.isLoading && unidades.length === 0 ? (
            <div className="table-empty">Sin unidades registradas.</div>
          ) : null}
          {unidades.map((unidad) => (
            <div key={unidad.id} className={`catalogo-row${unidad.activo ? '' : ' is-inactive'}`}>
              <span className="catalogo-code">{unidad.codigo}</span>
              <div className="catalogo-row__main">
                <strong>{unidad.nombre}</strong>
                <span>
                  {unidad.permiteDecimales ? 'Admite decimales' : 'Solo cantidades enteras'}
                </span>
              </div>
              <span className="pill pill--soft">{unidad.productos} prod.</span>
              {canManage ? (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={busyId === unidad.id}
                  onClick={() => void alternarUnidad(unidad.id, !unidad.activo)}
                >
                  {unidad.activo ? 'Desactivar' : 'Reactivar'}
                </button>
              ) : (
                <span className="status-badge status-badge--neutral">
                  {unidad.activo ? 'Activa' : 'Inactiva'}
                </span>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
